import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { hapticLight, hapticMedium } from '../../utils/nativeHaptics';
import { useScrollContainer, getScrollTop } from '../layout/ScrollContainer';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  disabled?: boolean;
  threshold?: number;
  maxPull?: number;
  pullIndicatorColor?: string;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  disabled = false,
  threshold = 80,
  maxPull = 120,
  pullIndicatorColor,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const isPullingRef = useRef<boolean>(false);
  const pullDistanceRef = useRef<number>(0);
  const isRefreshingRef = useRef<boolean>(false);

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  const indicatorColor = pullIndicatorColor || theme.palette.primary.main;
  const scrollSource = useScrollContainer();

  // Check if we should enable pull-to-refresh
  const shouldEnablePull = useCallback(() => {
    if (disabled || isRefreshingRef.current) return false;

    // Only enable on touch devices
    if (!('ontouchstart' in window) && navigator.maxTouchPoints <= 0) {
      return false;
    }

    // Check if we're at the top of the active scroll source (tab container or window)
    return getScrollTop(scrollSource) <= 0;
  }, [disabled, scrollSource]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!shouldEnablePull()) return;

    startYRef.current = e.touches[0].clientY;
    isPullingRef.current = true;
    setIsPulling(true);
  }, [shouldEnablePull]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPullingRef.current || isRefreshingRef.current) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;

    // Only allow pulling down when at top
    if (diff > 0 && shouldEnablePull()) {
      // Apply resistance to make it feel natural
      const resistance = 0.5;
      const adjustedDiff = Math.min(diff * resistance, maxPull);

      // Haptic feedback when threshold is crossed — use ref to avoid stale closure
      if (adjustedDiff >= threshold && pullDistanceRef.current < threshold) {
        void hapticLight();
      }

      pullDistanceRef.current = adjustedDiff;
      setPullDistance(adjustedDiff);

      // Prevent default scroll when pulling — must be called on the scroll
      // container itself (or an ancestor) to take effect on iOS/WKWebView
      if (adjustedDiff > 10) {
        e.preventDefault();
      }
    } else if (diff <= 0) {
      // Reset if scrolling up
      isPullingRef.current = false;
      pullDistanceRef.current = 0;
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [maxPull, shouldEnablePull, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPullingRef.current) return;

    isPullingRef.current = false;
    setIsPulling(false);

    const currentPullDistance = pullDistanceRef.current;

    if (currentPullDistance >= threshold && !isRefreshingRef.current) {
      isRefreshingRef.current = true;
      setIsRefreshing(true);
      pullDistanceRef.current = threshold;
      setPullDistance(threshold); // Keep indicator visible during refresh

      void hapticMedium();

      try {
        await onRefresh();
      } catch (error) {
        console.error('Pull to refresh error:', error);
      } finally {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    } else {
      // Animate back to 0
      pullDistanceRef.current = 0;
      setPullDistance(0);
    }
  }, [threshold, onRefresh]);

  // Attach touch event listeners to the scroll container (TabContainer element)
  // so that e.preventDefault() in touchmove can actually suppress the scroll.
  // Falls back to the inner containerRef when outside a TabContainer (window scrolls).
  useEffect(() => {
    const target: EventTarget = scrollSource ?? containerRef.current ?? document;

    target.addEventListener('touchstart', handleTouchStart as EventListener, { passive: true });
    target.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    target.addEventListener('touchend', handleTouchEnd as EventListener, { passive: true });

    return () => {
      target.removeEventListener('touchstart', handleTouchStart as EventListener);
      target.removeEventListener('touchmove', handleTouchMove as EventListener);
      target.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [scrollSource, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate indicator progress (0 to 1)
  const progress = Math.min(pullDistance / threshold, 1);
  const rotation = progress * 180;

  // Calculate safe area top (for notched devices)
  const safeAreaTop = 'var(--safe-area-inset-top)';

  return (
    <>
      {/* Pull indicator - rendered as a portal-like fixed element outside the content flow */}
      {(pullDistance > 0 || isRefreshing) && (
        <Box
          sx={{
            position: 'fixed',
            top: `calc(${safeAreaTop} + ${Math.min(pullDistance, threshold)}px - 50px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            pointerEvents: 'none',
            transition: isPulling ? 'none' : 'top 0.2s ease-out, opacity 0.2s ease-out',
            opacity: pullDistance > 20 || isRefreshing ? 1 : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: theme.palette.background.paper,
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={24} sx={{ color: indicatorColor }} />
            ) : (
              <RefreshIcon
                sx={{
                  color: indicatorColor,
                  fontSize: 24,
                  transform: `rotate(${rotation}deg)`,
                  transition: isPulling ? 'none' : 'transform 0.2s ease',
                  opacity: progress >= 1 ? 1 : 0.6,
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Content - NO transform applied to preserve fixed positioning */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          minHeight: '100%',
          touchAction: isPulling ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default PullToRefresh;
